/**
 * RichHtml — the single place we render user-authored / WYSIWYG HTML.
 *
 * Two guarantees:
 *   1. **Sanitisation** — every string is run through DOMPurify with a
 *      curated allow-list. `<script>` tags, `on*` event attributes, and
 *      `javascript:` URLs are stripped centrally so we can't leak XSS
 *      through any of the six-and-counting rich-HTML surfaces (custom
 *      pages, text modules, product descriptions, terms page, Tiptap
 *      previews, etc.). Callers can never opt out of sanitisation.
 *   2. **Blank-line preservation** — empty `<p></p>` and `<p><br></p>`
 *      get a `&nbsp;` inline so browsers don't collapse them. Paired
 *      with the global `data-rich-html` CSS rules in `App.css` for a
 *      belt-and-braces fix on legacy content.
 *
 * Anything untrusted (DB content, agent input, imported feed, etc.)
 * MUST render via this component — never bare `dangerouslySetInnerHTML`.
 */
import { useMemo, useRef, useState, useEffect } from "react";
import DOMPurify from "dompurify";
import { Dialog, DialogContent } from "@/components/ui/dialog";

// Empty-paragraph normalisation (run BEFORE sanitising so the added
// `&nbsp;` survives DOMPurify's whitespace handling).
const EMPTY_P = /<p>\s*<\/p>/gi;
const P_WITH_ONLY_BR = /<p>\s*<br\s*\/?>\s*<\/p>/gi;

export function preserveBlankLines(html) {
  if (!html || typeof html !== "string") return html || "";
  return html
    .replace(EMPTY_P, "<p>&nbsp;</p>")
    .replace(P_WITH_ONLY_BR, "<p>&nbsp;</p>");
}

/**
 * Markdown-lite bold shortcut: convert every `**text**` pair into
 * `<strong>text</strong>` so authors can bold copy without opening the
 * toolbar. Runs BEFORE DOMPurify so the resulting `<strong>` tags pass
 * through the allow-list.
 *
 * Intentional limits (matches the shorthand in property descriptions):
 *  - Content between markers can't contain `*`, `<`, `>` or a newline,
 *    so a stray `**` at end of line doesn't accidentally match across
 *    paragraphs / attributes.
 *  - Content inside <code> / <pre> is masked out first so code snippets
 *    that contain literal `**` (e.g. Python decorators, JS spread hints)
 *    are left untouched.
 */
export function convertInlineBold(html) {
  if (!html || typeof html !== "string") return html || "";
  const placeholders = [];
  // Mask <pre>…</pre> and <code>…</code> blocks so their contents are
  // opaque to the bold regex, then restore them at the end.
  const masked = html.replace(/<(pre|code)\b[^>]*>[\s\S]*?<\/\1>/gi, (m) => {
    placeholders.push(m);
    return `\u0000${placeholders.length - 1}\u0000`;
  });
  const bolded = masked.replace(/\*\*([^*<>\n]+?)\*\*/g, "<strong>$1</strong>");
  return bolded.replace(/\u0000(\d+)\u0000/g, (_, i) => placeholders[Number(i)] || "");
}

/**
 * Markdown-lite bullet shortcut: a line that starts with "* " (right
 * after an opening <p>/<li>, after a <br>, or at the very start of the
 * string) reads as a bullet — swap the literal asterisk for a tick
 * glyph instead of leaving a stray "*" in the rendered copy. Runs
 * BEFORE DOMPurify, same convention as `convertInlineBold` above.
 */
export function convertBulletStars(html) {
  if (!html || typeof html !== "string") return html || "";
  const placeholders = [];
  const masked = html.replace(/<(pre|code)\b[^>]*>[\s\S]*?<\/\1>/gi, (m) => {
    placeholders.push(m);
    return `\u0001${placeholders.length - 1}\u0001`;
  });
  const ticked = masked.replace(
    /(^|<p(?:\s[^>]*)?>|<li(?:\s[^>]*)?>|<br\s*\/?>)(\s*)\*\s+/gi,
    '$1$2<span class="rh-bullet-tick" aria-hidden="true">\u2713</span>&nbsp;'
  );
  return ticked.replace(/\u0001(\d+)\u0001/g, (_, i) => placeholders[Number(i)] || "");
}

// DOMPurify config — reasonable HTML allow-list for CMS content. We
// keep media (img/video/iframe) and structural elements but drop
// scripts, event handlers, and any inline JS URL. `iframe` is allowed
// because the CMS embeds YouTube; the sandbox attribute is preserved
// so authors can lock down what runs inside. SVG shape primitives are
// allow-listed so the Tiptap "Shapes" toolbar (rectangles, circles,
// arrows, lines) round-trips through sanitisation unchanged.
const PURIFY_CONFIG = {
  ADD_ATTR: [
    // Iframe / media
    "allow", "allowfullscreen", "frameborder", "loading", "referrerpolicy",
    // Anchor
    "target", "rel",
    // Tiptap-authored attributes
    "colwidth", "data-align", "data-type",
    // SVG shape primitives (via Tiptap Shapes toolbar)
    "viewBox", "preserveAspectRatio", "xmlns", "fill", "stroke",
    "stroke-width", "stroke-linecap", "stroke-linejoin",
    "cx", "cy", "r", "x", "y", "x1", "y1", "x2", "y2",
    "width", "height", "rx", "ry", "d", "points", "transform",
    "marker-end", "marker-start",
  ],
  ADD_TAGS: [
    "iframe",
    // Inline SVG shape primitives
    "svg", "g", "path", "rect", "circle", "ellipse", "line", "polyline",
    "polygon", "defs", "marker", "text", "tspan",
  ],
  // Keep target="_blank" but force safe rel to prevent tab-nabbing.
  ALLOW_UNKNOWN_PROTOCOLS: false,
  FORBID_TAGS: ["script", "style", "object", "embed", "form", "input", "textarea", "button"],
  FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus", "onblur", "formaction"],
};

// Enforce safe defaults on links after purification: any external
// target="_blank" gets rel="noopener noreferrer" so a rogue link can't
// hijack `window.opener`. Run once per hook via addHook so we don't
// reattach every sanitize() call.
if (typeof window !== "undefined" && !window.__slh_richhtml_hook__) {
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A") {
      const href = node.getAttribute("href") || "";
      if (/^\s*javascript:/i.test(href)) node.removeAttribute("href");
      if (node.getAttribute("target") === "_blank") {
        node.setAttribute("rel", "noopener noreferrer");
      }
    }
    // Force iframe sandboxing when the author didn't set one.
    if (node.tagName === "IFRAME" && !node.getAttribute("sandbox")) {
      node.setAttribute("sandbox", "allow-scripts allow-same-origin allow-popups allow-forms allow-presentation");
      node.setAttribute("referrerpolicy", node.getAttribute("referrerpolicy") || "no-referrer-when-downgrade");
    }
  });
  window.__slh_richhtml_hook__ = true;
}

export function sanitizeHtml(html) {
  if (!html || typeof html !== "string") return "";
  return DOMPurify.sanitize(html, PURIFY_CONFIG);
}

// Allow-list for any URL we open ourselves in an iframe lightbox (CTA
// "modal" target) — the sanitised href itself already blocks javascript:,
// but data-* attributes aren't scheme-checked by DOMPurify, so this is a
// second gate before handing a string to an <iframe src>.
export function isSafeCtaUrl(url) {
  if (!url || typeof url !== "string") return false;
  const trimmed = url.trim();
  // A leading "//" is protocol-relative (resolves to an attacker's own
  // domain), not a same-origin path — only a single leading "/" counts.
  if (trimmed.startsWith("/") && !trimmed.startsWith("//")) return true;
  if (trimmed.startsWith("#")) return true;
  return /^(https?|mailto|tel):/i.test(trimmed);
}

export default function RichHtml({ html, className = "", as: Tag = "div", ...rest }) {
  const safe = useMemo(
    () => sanitizeHtml(convertInlineBold(convertBulletStars(preserveBlankLines(html)))),
    [html],
  );
  const containerRef = useRef(null);
  const [activeVideo, setActiveVideo] = useState(null);
  const [activeCtaModal, setActiveCtaModal] = useState(null);

  // Popup-mode video embeds (`.video-embed-modal`) are just a static
  // thumbnail + play button in the sanitised HTML — wire up the actual
  // click-to-play lightbox here since dangerouslySetInnerHTML content
  // can't carry React handlers. Same for CTA buttons set to open in a
  // modal (`.slh-cta-modal`) — the destination URL opens in an iframe
  // lightbox instead of navigating away.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onClick = (e) => {
      const ctaTrigger = e.target.closest && e.target.closest(".slh-cta-modal");
      if (ctaTrigger) {
        e.preventDefault();
        const url = ctaTrigger.getAttribute("data-cta-url");
        if (isSafeCtaUrl(url)) setActiveCtaModal(url);
        return;
      }
      const trigger = e.target.closest && e.target.closest(".video-embed-modal");
      if (!trigger) return;
      e.preventDefault();
      const src = trigger.getAttribute("data-video-src");
      const kind = trigger.getAttribute("data-video-kind") || "iframe";
      if (src) setActiveVideo({ src, kind });
    };
    container.addEventListener("click", onClick);
    return () => container.removeEventListener("click", onClick);
  }, [safe]);

  return (
    <>
      <Tag
        {...rest}
        ref={containerRef}
        className={className}
        data-rich-html="true"
        dangerouslySetInnerHTML={{ __html: safe }}
      />
      <Dialog open={!!activeVideo} onOpenChange={(open) => !open && setActiveVideo(null)}>
        <DialogContent
          className="w-[92vw] sm:max-w-3xl sm:p-0 p-0 overflow-hidden rounded-none bg-black border-0"
          data-testid="video-lightbox-modal"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {activeVideo && (
            <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
              {activeVideo.kind === "file" ? (
                <video
                  src={activeVideo.src}
                  controls
                  autoPlay
                  className="absolute inset-0 w-full h-full"
                  data-testid="video-lightbox-player"
                />
              ) : (
                <iframe
                  src={activeVideo.src}
                  title="Video"
                  className="absolute inset-0 w-full h-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  data-testid="video-lightbox-player"
                />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!activeCtaModal} onOpenChange={(open) => !open && setActiveCtaModal(null)}>
        <DialogContent
          className="w-[92vw] sm:max-w-3xl h-[85vh] sm:p-0 p-0 overflow-hidden"
          data-testid="cta-lightbox-modal"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {activeCtaModal && (
            <iframe
              src={activeCtaModal}
              title="CTA destination"
              className="w-full h-full border-0"
              data-testid="cta-lightbox-frame"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
